import React from 'react';
import Module from 'Components/Core/Module';
import MarketdataGroupSelector from './MarketdataGroupSelector';
import './Marketdata.css';

// Lazy geladen (Code-Splitting) statt normaler Import - MarketdataDetailPanel
// zieht recharts nach, eine vergleichsweise schwere Chart-Bibliothek. Da alle
// Module beim App-Start gemountet werden (siehe Content.js), wuerde ein
// normaler Import recharts bei JEDEM Reload mitladen/parsen, auch wenn
// "Live-Kurse" gar nicht aktiv ist - das machte den Reload spuerbar
// langsamer. Mit React.lazy() laedt der Code erst, wenn tatsaechlich ein
// Symbol angeklickt und das Panel gerendert wird.
const MarketdataDetailPanel = React.lazy(() => import('./MarketdataDetailPanel'));

// FMP liefert fuer kanadische Boersen (TSX/TSXV/NEO/CNQ) ueber den
// aftermarket-quote-Endpunkt IMMER ein leeres Ergebnis (mehrfach mit
// verschiedenen Symbolen zu verschiedenen Zeitpunkten bestaetigt, auch
// waehrend regulaerer TSX-Handelszeit) - eine dauerhafte Datenluecke, kein
// Zeitfenster-Thema. Fuer US-Boersen liefert derselbe Endpunkt trotz des
// irrefuehrenden Namens auch waehrend der regulaeren Handelszeit Daten,
// daher hier keine falsche "nur vor-/nachbörslich"-Ankuendigung mehr, nur
// noch ein neutraler Hinweis fuer den (seltenen) Fall, dass gerade keine
// Daten vorliegen.
const CANADIAN_EXCHANGES = ['TSX', 'TSXV', 'NEO', 'CNQ'];

function bidAskHint(exchange) {
	return CANADIAN_EXCHANGES.includes(exchange) ? 'Bid/Ask für diese Börse nicht verfügbar' : 'Bid/Ask aktuell nicht verfügbar';
}

// Laedt beim Mount einen Snapshot (letzter bekannter Kurs je Symbol aus dem
// Redis-Cache, siehe MarketdataQuotes.inc) und haengt sich danach direkt an
// die bestehende Socket.IO-Verbindung (x.was.webSocket, siehe
// WebappWebSocket.js) fuer laufende Live-Updates - kein Polling.
// Desktop-Layout (ab 1025px, App-weite Grenze): Tabelle links (Hauptbereich) + Detail-/Chart-
// Panel rechts fuer das per Klick ausgewaehlte Symbol. Darunter (schmaler
// Viewport) stapelt sich das Panel unter die Tabelle statt daneben.
export default function MarketdataQuotes(props) {

	const x = props.x;
	const [quotes, setQuotes] = React.useState({});
	const previousPrices = React.useRef({});
	const [flashSymbols, setFlashSymbols] = React.useState({});
	const flashTimers = React.useRef({});
	const [selectedSymbol, setSelectedSymbol] = React.useState(null);
	const [reloading, setReloading] = React.useState(false);
	const symbolOrder = React.useRef([]);
	const [selectedGroupId, setSelectedGroupId] = React.useState(null);

	// Kurze visuelle Rueckmeldung ("hier kam gerade ein Update rein"), da bei
	// kurzem Poll-Intervall (siehe marketdataWorker.mjs) sonst nicht auffaellt,
	// wann genau sich ein Wert aktualisiert. Faerbung nach Richtung
	// (hoch/runter/gleich), automatisches Ausblenden nach 1s - rein CSS-basiert,
	// keine Hover-Abhaengigkeit, daher auch auf Touch-Geraeten sichtbar.
	function triggerFlash(symbol, direction) {
		setFlashSymbols((current) => ({ ...current, [symbol]: direction }));
		if (flashTimers.current[symbol]) {
			clearTimeout(flashTimers.current[symbol]);
		}
		flashTimers.current[symbol] = setTimeout(() => {
			setFlashSymbols((current) => {
				const next = { ...current };
				delete next[symbol];
				return next;
			});
			delete flashTimers.current[symbol];
		}, 1000);
	}

	React.useEffect(() => {
		return () => {
			Object.values(flashTimers.current).forEach((timer) => clearTimeout(timer));
		};
	}, []);

	function loadSnapshot(onDone) {
		if (selectedGroupId === null) {
			if (onDone) onDone();
			return;
		}
		x.was.axPost('Backend/webapp.php?module=Marketdata&task=MarketdataQuotes', { groupid: selectedGroupId }, (result) => {
			if (result.data.returncode !== 1) {
				if (onDone) onDone();
				return;
			}
			const rows = result.data.content || [];
			const initial = {};
			rows.forEach((row) => {
				initial[row.symbol] = row;
			});
			// Reihenfolge der Watchlist (sortorder, siehe MarketdataQuotes.inc)
			// separat merken - der quotes-State ist ein Objekt (Zugriff per
			// Symbol fuer Live-Updates), Objektschluessel haben aber keine
			// verlaessliche Reihenfolge fuer die Anzeige.
			symbolOrder.current = rows.map((row) => row.symbol);
			// Ersetzt den kompletten State (nicht mergen) - dadurch verschwinden
			// zwischenzeitlich aus der Watchlist geloeschte Symbole automatisch,
			// und neu hinzugefuegte tauchen auf.
			setQuotes(initial);
			setSelectedSymbol((current) => (current && initial[current] ? current : null));
			if (onDone) onDone();
		});
	}

	// Manueller Reload-Button - Watchlist-Aenderungen (Symbol hinzugefuegt/
	// entfernt) kommen nur ueber diesen Weg in eine bereits geoeffnete
	// Live-Kurse-Ansicht, da der Live-Update-Kanal (marketdata-quote) nur
	// Kursaenderungen fuer bereits bekannte Symbole liefert, keine
	// Watchlist-Strukturaenderungen.
	function reload() {
		setReloading(true);
		loadSnapshot(() => setReloading(false));
	}

	function handleQuote(args) {
		// Nur verarbeiten, wenn Kursdaten gerade das aktive/sichtbare Modul sind
		// (x.wam.current, siehe WebappModule.js) - Updates fuer ein Modul im
		// Hintergrund (z.B. waehrend man im Forum ist) werden ignoriert, statt
		// unnoetig State-Updates/Re-Renders fuer eine unsichtbare Tabelle
		// auszuloesen. Beim naechsten Aktivieren laedt loadSnapshot() ohnehin
		// den aktuellen Stand nach.
		if (x.wam.current !== props.id) {
			return;
		}
		const data = typeof args === 'string' ? JSON.parse(args) : args;
		// Der Live-Update-Kanal liefert Kursaenderungen fuer ALLE Symbole aller
		// Watchlists (der Node-Worker kennt keine Gruppen, siehe marketdataWorker.mjs).
		// symbolOrder.current (statt des quotes-States direkt) fuer die
		// Zugehoerigkeits-Pruefung nutzen, da handleQuote nur EINMAL beim Mounten
		// registriert wird (socket.on in der []-Effect unten) und damit sonst ueber
        // eine veraltete/leere quotes-Referenz aus dem allerersten Render
		// pruefen wuerde (stale closure) - jedes Update waere faelschlich
		// verworfen worden. symbolOrder.current wird von loadSnapshot() bei jedem
		// (Neu-)Laden aktuell gehalten, ist also immer auf dem neuesten Stand.
		if (!symbolOrder.current.includes(data.symbol)) {
			return;
		}
		let direction = 'flat';
		setQuotes((current) => {
			if (!(data.symbol in current)) {
				return current;
			}
			const previousPrice = current[data.symbol]?.price;
			previousPrices.current[data.symbol] = previousPrice;
			direction = previousPrice == null || data.price === previousPrice ? 'flat' : (data.price > previousPrice ? 'up' : 'down');
			// Spread von current[symbol] zuerst, damit Felder, die der Live-
			// Update-Payload nicht mitliefert (z.B. shortlabel - kennt nur die
			// DB/der initiale Snapshot, nicht der Node-Worker), erhalten bleiben.
			return { ...current, [data.symbol]: { ...current[data.symbol], ...data } };
		});
		triggerFlash(data.symbol, direction);
	}

	React.useEffect(() => {
		const connection = x.was.webSocket;
		if (connection) {
			connection.on('marketdata-quote', handleQuote);
		}
		return () => {
			if (connection) {
				connection.off('marketdata-quote', handleQuote);
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	React.useEffect(() => {
		if (selectedGroupId !== null) {
			loadSnapshot();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedGroupId]);

	// Watchlist-Reihenfolge (sortorder) statt alphabetischer Sortierung -
	// symbolOrder.current kommt aus dem letzten loadSnapshot(); Object.keys(quotes)
	// nur als Fallback fuer Symbole, die (theoretisch) noch nicht darin stehen.
	const symbols = symbolOrder.current.filter((symbol) => symbol in quotes)
		.concat(Object.keys(quotes).filter((symbol) => !symbolOrder.current.includes(symbol)));

	function formatNumber(value, digits = 2) {
		return value == null ? '–' : value.toLocaleString('de-DE', { minimumFractionDigits: digits, maximumFractionDigits: digits });
	}

	function formatVolume(value) {
		if (value == null) return '–';
		if (value >= 1_000_000) return (value / 1_000_000).toLocaleString('de-DE', { maximumFractionDigits: 1 }) + 'M';
		if (value >= 1_000) return (value / 1_000).toLocaleString('de-DE', { maximumFractionDigits: 1 }) + 'K';
		return formatNumber(value, 0);
	}

	return (
		<Module
			id={props.id}
			x={x}
			classes="module-marketdata-quotes"
			title={props.title}
			display={props.display}
			displayTitle="d-block"
			titleToolbar={
				<span className="module-table-title-toolbar">
					<MarketdataGroupSelector x={x} id={props.id} onSelect={setSelectedGroupId} />
					<i
						className="bi bi-pencil module-icon"
						title="Watchlist bearbeiten"
						onClick={() => x.wam.activate('marketdata-watchlist')}
					/>
					<i
						className={'bi bi-arrow-repeat module-icon' + (reloading ? ' marketdata-reload-spinning' : '')}
						title="Watchlist-Änderungen laden"
						onClick={reload}
					/>
				</span>
			}
		>
			<div className="marketdata-layout">
				<div className="marketdata-quotes-scroll">
					<table className="marketdata-quotes-table">
						<thead>
							<tr>
								<th>Kurzbez.</th>
								<th className="marketdata-col-numeric">Kurs</th>
								<th className="marketdata-col-numeric">Veränderung</th>
								<th className="marketdata-col-numeric">Bid</th>
								<th className="marketdata-col-numeric">Ask</th>
								<th className="marketdata-col-numeric">Low</th>
								<th className="marketdata-col-numeric">High</th>
								<th className="marketdata-col-numeric">Volumen</th>
								<th className="marketdata-col-update"></th>
								<th>Zeitpunkt</th>
							</tr>
						</thead>
						<tbody>
							{symbols.length === 0 &&
								<tr><td colSpan="10" className="marketdata-empty">Keine Kurse verfügbar. Watchlist prüfen oder kurz warten, bis die ersten Kurse eintreffen.</td></tr>
							}
							{symbols.map((symbol) => {
								const quote = quotes[symbol];
								const previous = previousPrices.current[symbol];
								const direction = previous == null || quote.price === previous ? '' : (quote.price > previous ? 'marketdata-price-up' : 'marketdata-price-down');
								const changeDirection = quote.change == null ? '' : (quote.change > 0 ? 'marketdata-price-up' : quote.change < 0 ? 'marketdata-price-down' : '');
								const flashDirection = flashSymbols[symbol];
								const flashClass = flashDirection ? 'marketdata-row-flash marketdata-row-flash-' + flashDirection : '';
								const selectedClass = symbol === selectedSymbol ? ' marketdata-row-selected' : '';
								return (
									<tr key={symbol} className={flashClass + selectedClass} onClick={() => setSelectedSymbol(symbol)}>
										<td className="marketdata-symbol">{quote.shortlabel || symbol}</td>
										<td className={'marketdata-col-numeric marketdata-price ' + direction}>{formatNumber(quote.price)}</td>
										<td className={'marketdata-col-numeric ' + changeDirection}>
											{quote.change == null ? '–' : (
												<React.Fragment>
													{formatNumber(quote.change)} ({formatNumber(quote.changePercentage, 2)}%)
												</React.Fragment>
											)}
										</td>
										<td className="marketdata-col-numeric marketdata-secondary">
											{quote.bidPrice != null ? formatNumber(quote.bidPrice) : <span title={bidAskHint(quote.exchange)}>–</span>}
										</td>
										<td className="marketdata-col-numeric marketdata-secondary">
											{quote.askPrice != null ? formatNumber(quote.askPrice) : <span title={bidAskHint(quote.exchange)}>–</span>}
										</td>
										<td className="marketdata-col-numeric marketdata-secondary">{formatNumber(quote.dayLow)}</td>
										<td className="marketdata-col-numeric marketdata-secondary">{formatNumber(quote.dayHigh)}</td>
										<td className="marketdata-col-numeric marketdata-secondary">{formatVolume(quote.volume)}</td>
										<td className="marketdata-col-update">
											<span className={'marketdata-update-dot' + (flashDirection ? ' marketdata-update-dot-active marketdata-update-dot-' + flashDirection : '')} />
										</td>
										<td className="marketdata-timestamp">{quote.timestamp ? new Date(quote.timestamp).toLocaleTimeString('de-DE') : ''}</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
				{selectedSymbol
					? (
						<React.Suspense fallback={<div className="marketdata-detail-panel marketdata-detail-empty">Lädt…</div>}>
							<MarketdataDetailPanel x={x} symbol={selectedSymbol} quote={quotes[selectedSymbol]} onClose={() => setSelectedSymbol(null)} />
						</React.Suspense>
					)
					: (
						<div className="marketdata-detail-panel marketdata-detail-empty">
							Symbol in der Tabelle anklicken, um Chart und alle Details anzuzeigen.
						</div>
					)
				}
			</div>
		</Module>
	)

};
